import React, { useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { initialize } from 'redux-form';
import ContributeFormTemplate from '../../templates/ContributeFormTemplate/ContributeFormTemplate';
// eslint-disable-next-line max-len
import ErrorContributeCertificationTemplate from '../../templates/ErrorContributeCertificationTemplate/ErrorContributeCertificationTemplate';
// eslint-disable-next-line max-len
import ContributeCloseButtonTemplate from '../../templates/ContributeCloseButtonTemplate/ContributeCloseButtonTemplate';
import useFetchAction from '../../../hooks/useFetchAction';
import {
  getFormCertification as getFormCertificationAction,
  createCertificationAnswer as createCertificationAnswerAction,
  getCertification as getCertificationAction,
  // v5 only -> allows an admin to load the question ...
  getCertificationQuestion as getCertificationQuestionAction,
  // v5 only -> ... and provide the reference answer
  updateCertificationQuestionAnswer as updateCertificationQuestionAnswerAction,
} from '../../../store/actions/certification';
import { processChoicesInjectionColumns, processDataColumns } from '../../../helpers/formTree';
import {
  currentCertificationSelector,
  certificationsDepletedSelector,
  hasReceivedNullCertificationSelector,
} from '../../../store/selectors/certification';
import Spinner from '../../atoms/Spinner/Spinner';
import useTranslate from '../../../hooks/useTranslate';
import { createToast } from '../../../store/actions/toast';
import { SUCCESS } from '../../../constants/toastType';

/*
 * Two functionally different flows share this page, selected by `mode`:
 *
 *   answering  - a CANDIDATE answers the questions of their own certification test.
 *                The server decides which question comes next, so validating an answer
 *                immediately chains to the following one, until there is none left.
 *
 *   publishing (v5 only) - a MANAGER defines the expected (reference) answer of ONE question of the
 *                certification referential, designated by the URL. No chaining to the next
 *                question.
 *
 * Side note : reading an already recorded answer is a third, separate flow, handled by
 * ContributeAnwserPage with answerOf 'certification'.
 *
 * Only the four values below differ between modes. Loading the form, computing the form
 * data and choice overrides, the error handling and the whole rendering are shared.
 */
const CERTIFICATION_MODES = {
  // The candidate's test: the question comes from the server, the answer is theirs
  answering: {
    fetchAction: getCertificationAction,
    fetchArgs: (params) => [params.certificationId],
    submitAction: createCertificationAnswerAction,
    submitArgs: (certification, values) => [
      certification.certificationId,
      certification.certificationUserId,
      certification.certificationQuestionId,
      certification.certificationUserQuestionId,
      values,
    ],
    // On the answering mode the candidate lands on the next question when he submits his answer
    // (the server decides which question comes next).
    jumpToNextQuestionAfterSubmit: true,
  },
  // v5 only (publishing mode)
  // The referential question: both the question and the answer are picked by the manager
  publishing: {
    fetchAction: getCertificationQuestionAction,
    fetchArgs: (params) => [params.certificationId, params.questionId],
    submitAction: updateCertificationQuestionAnswerAction,
    submitArgs: (certification, values) => [
      certification.certificationId,
      certification.certificationQuestionId,
      values,
    ],
    // On the publishing mode the manager only gets a success message when he submits the reference
    // answer.
    showSuccessMessage: true,
  },
};

function CertificationPage({ mode = 'answering' }) {
  const i18n = useTranslate();
  const params = useParams();
  const {
    fetchAction,
    fetchArgs,
    submitAction,
    submitArgs,
    jumpToNextQuestionAfterSubmit,
    showSuccessMessage,
  } = CERTIFICATION_MODES[mode];
  const currentCertification = useSelector(currentCertificationSelector);
  const certificationsDepleted = useSelector(certificationsDepletedSelector);
  const hasReceivedNullCertification = useSelector(hasReceivedNullCertificationSelector);
  const [
    getCertification,
    ,
    certificationError,
    certification,
    certificationStatus,
  ] = useFetchAction(fetchAction);
  const [getForm, formLoading, formError, form] = useFetchAction(getFormCertificationAction);
  const [
    createCertificationAnswer,
    ,
    certificationAnswerError,
    ,
    certificationAnswerStatus,
    resetCertificationAnswerStatus,
  ] = useFetchAction(submitAction);
  const getCertificationWithParams = useCallback(() => {
    getCertification(...fetchArgs(params), { ignoreError: true });
  }, [getCertification, params.certificationId, params.questionId]);
  const dispatch = useDispatch();

  // Creates a key-value pair of task data
  const formData = useMemo(() => processDataColumns(certification?.columns), [
    certification?.certificationId,
  ]);

  // Generate choice inject overrides
  const certificationChoicesData = useMemo(
    () => processChoicesInjectionColumns(currentCertification?.columns),
    [currentCertification?.certificationQuestionId],
  );

  // First we load the task
  useEffect(() => {
    getCertificationWithParams(params.certificationId);
  }, []);

  // Once task is loaded, load the form
  useEffect(() => {
    if (certification?.formId && !form && !formLoading) {
      getForm(certification.formId);
    }
  }, [certification, form, formLoading]);

  // PUT task to backend
  const handleSubmit = useCallback(
    (values) => {
      createCertificationAnswer(...submitArgs(certification, values), { ignoreError: true });
    },
    [certification?.certificationId],
  );

  // Immediatly load a new task when an answer is succesfully submited
  useEffect(() => {
    if (certificationAnswerStatus === 'success') {
      if (showSuccessMessage) {
        createToast(SUCCESS, i18n('CertificationPage.published'));
      }
      if (jumpToNextQuestionAfterSubmit) {
        // Clear form state
        dispatch(initialize('contribute'));
        if (!hasReceivedNullCertification) {
          // Retrieved a new task in simple mode
          getCertificationWithParams(params.certificationId);
        }
      }
      resetCertificationAnswerStatus();
    }
  }, [certificationAnswerStatus, hasReceivedNullCertification]);
  if (!certificationsDepleted && !certificationError && !formError && !certificationAnswerError) {
    if (
      (certificationStatus === 'pending' && !currentCertification) ||
      !form ||
      (jumpToNextQuestionAfterSubmit &&
        (certificationAnswerStatus === 'pending' || certificationAnswerStatus === 'success'))
    ) {
      return <Spinner />;
    }
  }
  return (
    <>
      {currentCertification && (
        <ContributeCloseButtonTemplate
          taskId={currentCertification.certificationQuestionId}
          projectId={currentCertification.certificationId}
        />
      )}
      <ErrorContributeCertificationTemplate
        certificationsDepleted={certificationsDepleted}
        certificationError={certificationError}
        certificationAnswerError={certificationAnswerError}
        formError={formError}
      >
        {form && (
          <ContributeFormTemplate
            data={formData}
            form={form}
            answer={currentCertification?.answer}
            choicesOverrides={certificationChoicesData}
            onSubmit={handleSubmit}
          />
        )}
      </ErrorContributeCertificationTemplate>
    </>
  );
}

CertificationPage.propTypes = {
  mode: PropTypes.oneOf(['answering', 'publishing']),
};

export default CertificationPage;
